import { canonicalPrinting } from "@/utils/printingIdentity";
import { ResponsiveModal } from "@/components/ResponsiveModal/ResponsiveModal";
import { Tables } from "database.types";
import { useBinders } from "@/hooks/useBinders";
import { useCallback, useEffect, useMemo, useState } from "react";
import useVariantsCards from "@/hooks/useVariantsCards";
import { sortVariantsByConditions } from "@/utils/sortVariantsByConditions";
import { useGradingCompanies } from "@/hooks/useGradingCompanies";
import { sortGraders } from "@/utils/sortGraders";
import { useGradesQuery } from "@/queries/useGradesQuery";
import { sortGrades } from "@/utils/sortGrades";
import { Label } from "@radix-ui/react-label";
import { Input } from "@/components/ui/input";
import { usePriceInput } from "@/utils/usePriceInput";
import { formatPrice } from "@/utils/formatPrice";
import { Button } from "@/components/ui/button";
import { useGradedPricesStore } from "@/stores/useGradedPricesStore";
import { Spinner } from "@/components/ui/spinner";
import { AddCardToBinderDialogSkeleton } from "./AddCardToBiderDialogSkeleton";
import { AddCardToBinderBindersInput } from "../AddCardToBinderBindersInput/AddCardToBinderBindersInput";
import { AddCardToBinderOptions } from "../AddCardToBinderOptions/AddCardToBinderOptions";
import { AddCardToBinderQuantityInput } from "../AddCardToBinderQuantityInput/AddCardToBinderQuantityInput";
import { getCardById } from "@/api/cards";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { addVariantToBinder } from "@/api/binders";
import { AddVariantToBinderOptions } from "@/types/binders";
import { X } from "lucide-react";
import { AddCardToBinderTotalPrice } from "../AddCardToBinderTotalPrice/AddCardToBinderTotalPrice";
import { invalidateBinderWatchlistVariantsQueries } from "@/queries/common";

import type { ScanIdentity } from "@/types/scan";
import { selectScanVariant } from "@/utils/selectScanVariant";

interface Props {
  identityConfirmed?: boolean;
  inline?: boolean;
  onAdded?: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cardId: number;
  gradingCompanyId?: string | null;
  gradeId?: string | null;
  scanId?: number | null;
  scanIdentity?: ScanIdentity;
  variantId?: number | null;
  binderId?: number | null;
}

export function AddCardToBinderDialog({
  cardId,
  identityConfirmed = false,
  inline = false,
  onAdded,
  open,
  onOpenChange,
  gradingCompanyId: gradingCompanyIdProp = null,
  gradeId: gradeIdProp = null,
  scanId,
  scanIdentity,
  variantId: variantIdProp = null,
  binderId: binderIdProp = null,
}: Props) {
  const { load: loadGradedPrice, prices: gradedPrices } =
    useGradedPricesStore();

  const [isShowAddedAnimation, setIsShowAddedAnimation] = useState(false);

  const [wasSuccess, setWasSuccess] = useState(false);
  const [gradingConfirmed, setGradingConfirmed] = useState(false);
  useEffect(() => {
    setGradingConfirmed(scanIdentity?.slab?.status === "confirmed");
  }, [open, cardId, scanId, scanIdentity?.slab?.status]);

  const [validationErrorMessage, setValidationErrorErrorMessage] = useState<
    string | null
  >(null);

  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending,
    reset: resetMutation,
  } = useMutation({
    mutationFn: (options: AddVariantToBinderOptions[]) => {
      return Promise.all(options.map((option) => addVariantToBinder(option)));
    },
    onSuccess() {
      onSuccess();
      invalidateBinderWatchlistVariantsQueries();
    },
  });

  const {
    data: catalogResponse,
    isLoading: isLoadingCatalog,
    isError: isCatalogError,
  } = useQuery({
    queryKey: ["card", cardId],
    queryFn: () => getCardById(cardId),
    enabled: open,
  });
  const catalogCard = catalogResponse?.data;
  const catalogLanguage = catalogCard?.language;
  const catalogLanguageLocked = ["tcgdex", "ximilar"].includes(
    catalogCard?.catalog_source ?? "",
  );

  const { data: binders = [], isLoading: isLoadingBinders } = useBinders();

  const { data: variants = [], isLoading: isLoadingVariants } =
    useVariantsCards({
      cardId,
      enabled: open,
    });

  const { data: gradingCompanies, isLoading: isLoadingGradingCompanies } =
    useGradingCompanies();

  const { data: grades, isLoading: isLoadingGrades } = useGradesQuery();

  const [selectedVariantId, setSelectedVariantId] = useState<
    Tables<"variants">["id"] | null
  >(null);

  const [purchasePriceInput, setPurchasePriceInput] = usePriceInput("", {
    min: 0.01,
  });

  const [selectedBinderId, setSelectedBinderId] = useState<
    Tables<"binders">["id"] | null
  >(null);

  const [quantityInput, setQuantityInput] = useState(1);

  const [selectedLanguage, setSelectedLanguage] = useState<
    "english" | "japanese" | "chinese"
  >("english");

  useEffect(() => {
    if (
      open &&
      (catalogLanguage === "english" ||
        catalogLanguage === "japanese" ||
        catalogLanguage === "chinese")
    ) {
      setSelectedLanguage(catalogLanguage);
    }
  }, [open, cardId, catalogLanguage]);

  const languageOptions = [
    { id: "english", label: "English" },
    { id: "japanese", label: "Japanese" },
    { id: "chinese", label: "Chinese" },
  ];

  const [selectedGradingCompanyId, setSelectedGradingCompanyId] = useState<
    Tables<"grading_companies">["id"] | null
  >(null);

  const [selectedGradeId, setSelectedGradeId] = useState<
    Tables<"grades">["id"] | null
  >(null);

  const foreignCatalog = ["japanese", "chinese"].includes(catalogLanguage ?? "");
  const [physicalPrinting, setPhysicalPrinting] = useState<string | null>(null);
  const physicalPrintingOptions = [
    { id: "Normal", label: "Normal / Non-foil" },
    { id: "Holofoil", label: "Holofoil" },
    { id: "Reverse Holofoil", label: "Reverse Holofoil" },
    { id: "Poke Ball Holofoil", label: "Poké Ball Holofoil" },
    { id: "Master Ball Holofoil", label: "Master Ball Holofoil" },
    { id: "Unspecified", label: "Unknown — save without a price" },
  ];

  const selectedVariant = useMemo(() => {
    return variants.find((variant) => variant.id === selectedVariantId);
  }, [selectedVariantId, variants]);

  useEffect(() => {
    setPhysicalPrinting(canonicalPrinting(selectedVariant?.printing?.name));
  }, [selectedVariantId, selectedVariant?.printing?.name]);

  const gradingCompanyOptions = useMemo(() => {
    const sortedGradingCompanies = sortGraders(gradingCompanies?.data || []);

    return sortedGradingCompanies.map((gradingCompany) => ({
      id: gradingCompany.id,
      label: gradingCompany.name,
    }));
  }, [gradingCompanies]);

  const variantOptions = useMemo(() => {
    const sortedVariants = sortVariantsByConditions(variants);

    return sortedVariants.map((variant) => {
      const name = variant.condition.name;
      const price = variant.price;
      const formattedPrice = price ? formatPrice(price) : "";

      const printing = variant.printing?.name;
      const description = printing ? `${name} · ${printing}` : name;
      const label = formattedPrice
        ? `${description} (${formattedPrice})`
        : description;

      return {
        id: variant.id,
        label,
      };
    });
  }, [variants]);

  const gradeOptions = useMemo(() => {
    const sortedGrades = sortGrades(grades || []);

    if (!sortedGrades) {
      return [];
    }

    return sortedGrades.map((grade) => ({
      id: grade.id,
      label: grade.name,
    }));
  }, [grades]);

  const sortedBinders = useMemo(() => {
    return [...binders].sort((a, b) => {
      return Number(b.card_count) - Number(a.card_count);
    });
  }, [binders]);

  useEffect(() => {
    if (variantOptions.length > 0 && !selectedVariantId && !isLoadingCatalog) {
      setSelectedVariantId(
        selectScanVariant(
          sortVariantsByConditions(variants),
          scanIdentity,
          catalogCard?.set?.name,
          catalogLanguage,
        ),
      );
    }

    if (sortedBinders.length > 0 && !selectedBinderId) {
      setSelectedBinderId(sortedBinders[0].id);
    }
  }, [
    selectedBinderId,
    selectedVariantId,
    sortedBinders,
    variantOptions,
    variants,
    scanIdentity,
    catalogCard?.set?.name,
    catalogLanguage,
    isLoadingCatalog,
  ]);

  useEffect(() => {
    if (
      scanIdentity?.pricingEligible !== false &&
      selectedGradeId &&
      selectedGradingCompanyId
    ) {
      loadGradedPrice(cardId, selectedGradingCompanyId, selectedGradeId);
    }
  }, [
    selectedGradeId,
    selectedGradingCompanyId,
    cardId,
    loadGradedPrice,
    scanIdentity?.pricingEligible,
  ]);

  const isLoading =
    isLoadingCatalog ||
    isLoadingBinders ||
    isLoadingVariants ||
    isLoadingGradingCompanies ||
    isLoadingGrades;

  const isDisabled = isPending || isShowAddedAnimation || isCatalogError;

  const reset = useCallback(() => {
    setSelectedVariantId(null);
    setSelectedGradingCompanyId(null);
    setSelectedGradeId(null);
    setQuantityInput(1);
    setPurchasePriceInput("");
    resetMutation();
  }, [setPurchasePriceInput, resetMutation]);

  const onSuccess = useCallback(() => {
    setWasSuccess(true);
    onAdded?.();
    setIsShowAddedAnimation(true);
    setTimeout(() => {
      setIsShowAddedAnimation(false);
      reset();
    }, 2500);
  }, [reset, onAdded]);

  useEffect(() => {
    if (gradingCompanyIdProp) {
      setSelectedGradingCompanyId(gradingCompanyIdProp);
    }
    if (gradeIdProp) {
      setSelectedGradeId(gradeIdProp);
    }

    if (variantIdProp) {
      setSelectedVariantId(variantIdProp);
    }

    if (binderIdProp) {
      setSelectedBinderId(binderIdProp);
    }
  }, [
    gradingCompanyIdProp,
    gradeIdProp,
    setSelectedGradingCompanyId,
    setSelectedGradeId,
    variantIdProp,
    binderIdProp,
  ]);

  const onSubmit = useCallback(async () => {
    setValidationErrorErrorMessage(null);

    if (!selectedBinderId || !selectedVariantId) {
      setValidationErrorErrorMessage("Select the card's condition and printing before saving.");
      return;
    }
    if (foreignCatalog && !physicalPrinting) {
      setValidationErrorErrorMessage("Confirm the physical printing, or choose Unknown to save without a price.");
      return;
    }

    if (
      scanIdentity?.slab?.detected &&
      (!selectedGradingCompanyId || !selectedGradeId || !gradingConfirmed)
    ) {
      setValidationErrorErrorMessage(
        "Confirm the slab's grader and grade before saving. A graded scan cannot be saved as raw.",
      );
      return;
    }

    if (selectedGradeId && !selectedGradingCompanyId) {
      setValidationErrorErrorMessage("Please select a grader");
      return;
    }

    if (selectedGradingCompanyId && !selectedGradeId) {
      setValidationErrorErrorMessage("Please select a grade");
      return;
    }

    const data = {
      binderId: selectedBinderId,
      variantId: selectedVariantId,
      printing: foreignCatalog ? physicalPrinting ?? undefined : undefined,
      quantity: quantityInput,
      purchasePrice: purchasePriceInput ? Number(purchasePriceInput) : null,
      language: selectedLanguage,
      gradingCompanyId: selectedGradingCompanyId,
      gradeId: selectedGradeId,
      scanned: Boolean(scanId),
      scanId,
    };

    const allEntries = Array.from({ length: quantityInput }, (_, index) => {
      return {
        ...data,
      };
    });

    await mutateAsync(allEntries);
  }, [
    mutateAsync,
    purchasePriceInput,
    quantityInput,
    selectedBinderId,
    selectedLanguage,
    selectedGradeId,
    selectedGradingCompanyId,
    selectedVariantId,
    foreignCatalog,
    physicalPrinting,
    scanId,
    scanIdentity,
    gradingConfirmed,
  ]);

  const onClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  if (isLoading) {
    return (
      <ResponsiveModal
        inline={inline}
        open={open}
        onOpenChange={onOpenChange}
        title="Add to Binder"
      >
        <AddCardToBinderDialogSkeleton />
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      inline={inline}
      open={open}
      onOpenChange={onOpenChange}
      title="Add to Binder"
      repositionInputs={false}
    >
      <div className="grid gap-4">
        {scanIdentity && (
          <div className="rounded-md border p-3 text-sm">
            <p>
              Scan:{" "}
              {scanIdentity.chineseScript
                ? `${scanIdentity.chineseScript} Chinese`
                : scanIdentity.language}{" "}
              · {scanIdentity.cardFormat ?? "format needs confirmation"}
            </p>
            {scanIdentity.slab?.detected && (
              <>
                <p>
                  Label: {scanIdentity.slab.company ?? "Unknown grader"}{" "}
                  {scanIdentity.slab.grade ?? "Unreadable grade"}
                </p>
                {scanIdentity.slab.certificateNumber && (
                  <p>
                    Certificate read: {scanIdentity.slab.certificateNumber} (not
                    authenticated)
                  </p>
                )}
                {scanIdentity.slab.status === "confirmed" ? (
                  <p className="mt-2 text-green-600">
                    Grader and label grade recognized automatically.
                  </p>
                ) : (
                  <label className="mt-2 flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={gradingConfirmed}
                      onChange={(event) =>
                        setGradingConfirmed(event.target.checked)
                      }
                      disabled={isDisabled}
                    />
                    I checked the slab label and confirm the selected grader and
                    grade.
                  </label>
                )}
              </>
            )}
          </div>
        )}
        <AddCardToBinderOptions
          options={variantOptions}
          value={selectedVariantId}
          onChange={setSelectedVariantId}
          label="Variant"
          cancelable={false}
          disabled={isDisabled}
        />

        {foreignCatalog && (
          <div className="space-y-2">
            <AddCardToBinderOptions
              options={physicalPrintingOptions}
              value={physicalPrinting}
              onChange={setPhysicalPrinting}
              label="Physical printing"
              cancelable={false}
              disabled={isDisabled}
            />
            <p className="text-xs text-muted-foreground">
              Check the finish on your card. Art rarity does not confirm its foil pattern.
              Unknown printing can be saved, but its price will remain unavailable.
            </p>
          </div>
        )}

        <AddCardToBinderOptions
          options={languageOptions}
          value={selectedLanguage}
          onChange={(value) =>
            setSelectedLanguage(value as "english" | "japanese" | "chinese")
          }
          label="Card language"
          cancelable={false}
          disabled={isDisabled || catalogLanguageLocked}
        />

        <AddCardToBinderOptions
          options={gradingCompanyOptions}
          value={selectedGradingCompanyId}
          onChange={setSelectedGradingCompanyId}
          label="Grader"
          cancelable
          disabled={isDisabled}
        />

        <AddCardToBinderOptions
          options={gradeOptions}
          value={selectedGradeId}
          onChange={setSelectedGradeId}
          label="Grade"
          cancelable
          disabled={isDisabled}
        />
        <div className="grid gap-2">
          <Label className="font-medium">Purchase Price ($, optional)</Label>
          <Input
            placeholder=""
            value={purchasePriceInput}
            onChange={(e) => setPurchasePriceInput(e.target.value)}
            disabled={isDisabled}
          />
        </div>
        <div className="grid gap-2">
          <Label className="font-medium">Quantity</Label>
          <div className="flex items-center justify-between gap-2">
            <AddCardToBinderQuantityInput
              value={quantityInput}
              onChange={setQuantityInput}
              min={1}
              max={20}
              disabled={isDisabled}
            />
            {cardId && selectedVariant && (
              <AddCardToBinderTotalPrice
                cardId={cardId}
                chineseScript={scanIdentity?.chineseScript ?? undefined}
                labelTier={scanIdentity?.slab?.labelTier ?? undefined}
                language={selectedLanguage}
                selectedVariant={foreignCatalog && physicalPrinting ? {
                  ...selectedVariant, printing: { ...selectedVariant.printing, name: physicalPrinting },
                } : selectedVariant}
                gradingCompanyId={selectedGradingCompanyId}
                gradeId={selectedGradeId}
                quantity={quantityInput}
                pricingEligible={
                  (!scanIdentity || scanIdentity.status === "exact_match" || identityConfirmed) && (!foreignCatalog || Boolean(physicalPrinting && physicalPrinting !== "Unspecified"))
                }
              />
            )}
          </div>
        </div>
        {sortedBinders.length === 0 && (
          <div className="grid gap-2">
            <p>No binders found</p>
            <p className="text-muted-foreground">
              Please create a binder first
            </p>
          </div>
        )}
        {sortedBinders.length > 0 && (
          <>
            <div className="grid gap-2">
              <Label className="font-medium">Binder</Label>
              <AddCardToBinderBindersInput
                options={sortedBinders}
                value={selectedBinderId}
                onChange={setSelectedBinderId}
                disabled={isDisabled}
              />
            </div>

            {isCatalogError && (
              <div className="text-red-500">
                Could not load the card language. Close and try again before
                adding this card.
              </div>
            )}
            {validationErrorMessage && (
              <div className="text-red-500">{validationErrorMessage}</div>
            )}

            {!isShowAddedAnimation && (
              <Button
                className="w-full"
                disabled={isDisabled}
                onClick={onSubmit}
              >
                {isPending && <Spinner className="w-4 h-4 text-white" />}
                Add to Binder
              </Button>
            )}

            {isShowAddedAnimation && (
              <Button className="w-full cursor-default" variant="success">
                Added
              </Button>
            )}

            {wasSuccess && (
              <Button
                className="w-full cursor-default"
                variant="ghost"
                onClick={onClose}
              >
                <X className="w-4 h-4" />
                Close
              </Button>
            )}
          </>
        )}
      </div>
    </ResponsiveModal>
  );
}

